import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretDownIcon } from '../Icons';
import ChevronLeftIcon from '../Icons/ChevronLeft';

interface BreadcrumbPath {
  label: string;
  path: string | undefined;
  onClick?: (index?: number) => void;
}

interface BreadcrumbsProps {
  paths: BreadcrumbPath[];
  variant?: 'default' | 'back';
  onBack?: () => void;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ paths, variant = 'default', onBack }) => {
  const navigate = useNavigate();
  const url = new URL(window.location.href);
  const navigateToPath = (path: string) => navigate({ pathname: path, search: url.search });

  const handleOnClick = (
    path: string | undefined,
    customHandler?: (index: number) => void,
    index?: number
  ) => {
    if (customHandler && index !== undefined) {
      customHandler(index);
    }

    if (!path) {
      return;
    }

    navigateToPath(path);
  };

  if (variant === 'back') {
    const first = paths[0];
    if (!first) {
      return null;
    }
    return (
      <button
        type="button"
        className="absolute top-6 left-4 flex items-center gap-1 cursor-pointer text-primary-blue"
        onClick={() => (onBack ? onBack() : handleOnClick(first.path, first.onClick, 0))}
        aria-label={`Go back to ${first.label}`}>
        <ChevronLeftIcon variant="inherit" size="xxs" />
        <span className="text-xs font-medium text-emerge-navy hover:text-emerge-teal cursor-pointer">
          {first.label}
        </span>
      </button>
    );
  }

  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-2 text-xs">
        {paths.length === 1 && (
          <li key={`breadcrumb-0`}>
            <button
              type="button"
              className="flex items-center cursor-pointer gap-1 font-medium text-emerge-navy hover:text-emerge-teal md:ms-2 font-['General_Sans']"
              onClick={() => handleOnClick(paths[0].path, paths[0].onClick, 0)}
              aria-label={`Navigate to ${paths[0].label}`}>
              <ChevronLeftIcon variant="inherit" size="xxs" />
              <span className="ms-1">{paths[0].label}</span>
            </button>
          </li>
        )}

        {paths.length > 1 &&
          paths.map((path, index) => {
            if (index === 0) {
              return (
                <li className="inline-flex items-center" key={`breadcrumb-${index}`}>
                  <button
                    type="button"
                    onClick={() => handleOnClick(path.path, path.onClick, index)}
                    className="inline-flex items-center font-medium text-emerge-navy hover:text-emerge-teal cursor-pointer font-['General_Sans']"
                    aria-label={`Navigate to ${path.label}`}>
                    {path.label}
                  </button>
                </li>
              );
            }

            return (
              <li key={`breadcrumb-${index}`}>
                <div className="flex items-center">
                  <CaretDownIcon className="-rotate-90" size="xxs" />

                  <button
                    type="button"
                    onClick={() => handleOnClick(path.path, path.onClick, index)}
                    className="ms-1 font-medium text-emerge-navy hover:text-emerge-teal md:ms-2 cursor-pointer font-['General_Sans']"
                    aria-label={`Navigate to ${path.label}`}>
                    {path.label}
                  </button>
                </div>
              </li>
            );
          })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
